import { CompanionFace, type CompanionFaceMood } from '@/components/companion/feed/CompanionFace';
import {
  resolveCompanionTriggerState,
  visibleCompanionActionCount,
  type CompanionTriggerState,
} from '@/components/companion/feed/companion-feed-state';
import {
  COMPANION_PANEL_ID,
  COMPANION_TRIGGER_ID,
} from '@/components/companion/feed/companion-panel-ids';
import { useAuth } from '@/contexts/auth-context';
import { useCompanionFeedQuery } from '@/hooks/queries/use-companion-feed-query';
import { useCurrentBrandQuery } from '@/hooks/queries/use-current-brand-query';
import { companionPanelScope, useCompanionPanelStore } from '@/stores/companion-panel-store';
import { Button, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

/**
 * The companion's place in the top bar.
 *
 * It exists for every brand the companion answers for, including a brand with
 * nothing to show: a surface that disappears on a quiet day is a surface nobody
 * learns to look at. A brand outside the rollout gets no trigger at all, which
 * is how the kill switch and a cohort exit take the whole feature back.
 */

const MOOD_BY_STATE: Record<CompanionTriggerState, CompanionFaceMood> = {
  opportunities: 'alert',
  firstRun: 'look',
  needsCheck: 'doze',
  idle: 'doze',
};

export const CompanionTrigger = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: brand } = useCurrentBrandQuery();
  const brandId = brand?.id ?? '';
  const { data: feed } = useCompanionFeedQuery(brandId);

  const scope = companionPanelScope(user?.id ?? '', brandId);
  const open = useCompanionPanelStore((state) => state.byScope[scope]?.open ?? false);
  const togglePanel = useCompanionPanelStore((state) => state.togglePanel);

  if (!user?.id || !brandId || !feed?.availability.enabled) return null;

  const state = resolveCompanionTriggerState(feed);
  const label =
    state === 'opportunities'
      ? t('companion.trigger.opportunities', { count: visibleCompanionActionCount(feed) })
      : state === 'firstRun'
        ? t('companion.trigger.analysing')
        : state === 'needsCheck'
          ? t('companion.trigger.needsCheck')
          : null;

  return (
    <Button
      id={COMPANION_TRIGGER_ID}
      data-testid="companion-trigger"
      data-state={state}
      variant="plain"
      h="36px"
      minW="36px"
      pl="6px"
      pr={label ? '12px' : '6px'}
      gap="8px"
      borderRadius="9999px"
      bg="announcement.chipBg"
      color="announcement.indicatorFg"
      _hover={{ bg: 'announcement.controlHoverBg' }}
      aria-expanded={open}
      aria-controls={COMPANION_PANEL_ID}
      aria-label={label ?? t('companion.trigger.idleLabel')}
      onClick={() => togglePanel(scope)}
    >
      <CompanionFace mood={MOOD_BY_STATE[state]} size={24} />
      {label ? (
        <Text
          fontFamily="heading"
          fontSize="13px"
          fontWeight={state === 'opportunities' ? '600' : '500'}
          color={
            state === 'opportunities' ? 'announcement.indicatorFg' : 'announcement.indicatorFgMuted'
          }
          lineHeight="1"
        >
          {label}
        </Text>
      ) : null}
    </Button>
  );
};
